/**
 * Pemeriksa satu kurva: isinya sebagai grafik, atau sebagai angka.
 *
 * Pohon proyek menyebutkan kurva mana saja yang ada pada sumur aktif, tetapi
 * tidak pernah menunjukkan isinya. Untuk memastikan sebuah kurva memang berisi
 * — bukan seluruhnya kosong, bukan tertukar satuannya, bukan hasil hitung yang
 * mendatar karena satu parameter — satu-satunya cara adalah menaruhnya di trek
 * lembar log dan memandanginya. Itu terlalu jauh untuk pertanyaan "apa isinya".
 *
 * Dua tampilan, sebab keduanya menjawab pertanyaan yang berbeda. Grafik
 * menjawab bentuk: di mana ia berubah, di mana ia rumpang, apakah ia mendatar.
 * Tabel menjawab nilai: berapa persisnya di kedalaman itu — pertanyaan yang
 * tidak pernah bisa dijawab grafik betapapun besarnya.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "../api";
import { useAsync, useStored } from "../hooks";
import {
  BoxZoomOverlay,
  DEPTH_GRID_PX,
  DEPTH_GUTTER_W,
  DepthGutter,
  LogTrack,
  TrackHeader,
  type TrackCurve,
  type TrackSpec,
} from "./LogTracks";
import { depthTickCount } from "../lib/depthscale";
import { useDepthSheet } from "../lib/depthsheet";
import { applyDraft, editAt, valueFraction } from "../lib/curveedit";
import {
  resolveAxisRange,
  suggestedScale,
  type CurveFill,
  type CurveStyle,
} from "../lib/tracklayout";
import { Button, Dropdown, Empty, KeyValue, Loading, Modal, Notice, Segmented } from "./ui";

/**
 * Setelan tampilan satu kurva di jendela ini.
 *
 * Bentuknya sepotong `CurveConfig` lembar log, dan itu disengaja: yang
 * digambar di sini adalah track lembar log yang sama, jadi setelan yang
 * dikenalnya harus setelan yang sama pula. `fill` boleh `null` dengan arti yang
 * berbeda dari kosong — kosong berarti "belum disetel, pakai kelaziman
 * mnemonicnya", `null` berarti "sudah disetel, dan yang diminta tidak ada" —
 * sebab `undefined` lenyap ketika ditulis sebagai JSON, dan arsiran yang
 * sengaja dimatikan akan kembali dengan sendirinya pada kunjungan berikutnya.
 */
interface InspectConfig {
  color?: string;
  style?: CurveStyle;
  width?: number;
  min?: number;
  max?: number;
  logarithmic?: boolean;
  fill?: CurveFill | null;
}

interface CurveData {
  id: number;
  mnemonic: string;
  unit: string;
  description: string;
  depth_start: number;
  depth_stop: number;
  depth_step: number;
  count: number;
  display_min: number | null;
  display_max: number | null;
  color: string;
  depths: number[];
  values: number[];
}

/**
 * Nilai pembanding pada kedalaman kurva utama.
 *
 * Dua versi kurva yang sama sering tidak duduk di sumbu kedalaman yang sama:
 * setiap perhitungan menerbitkan sumbu yang membentang menutupi set sebelumnya
 * dan selang yang baru dihitung. Memasangkan menurut nomor baris karena itu
 * akan membandingkan kedalaman yang berbeda dan menyebutnya selisih.
 *
 * Rumpang tidak dijembatani: sebuah nilai hanya diberikan bila kedua cuplikan
 * pengapitnya berangka dan jaraknya masih wajar. Selisih yang dihitung terhadap
 * nilai hasil menjembatani seratus meter kosong adalah selisih yang dikarang.
 */
function sampleAt(depths: number[], values: (number | null)[], md: number): number | null {
  if (!depths.length) return null;
  if (md < depths[0] || md > depths[depths.length - 1]) return null;
  let lo = 0;
  let hi = depths.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (depths[mid] <= md) lo = mid;
    else hi = mid;
  }
  const a = values[lo];
  const b = values[hi];
  if (a === null || b === null || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  const span = depths[hi] - depths[lo];
  if (span <= 0) return a;
  // Dua cuplikan yang terpisah lebih dari satu meter bukan tetangga; di
  // antaranya ada rumpang, dan apa pun yang ditaruh di sana adalah karangan.
  if (span > 1) return null;
  const t = (md - depths[lo]) / span;
  return a + t * (b - a);
}

/** Angka yang muat di kolom sempit tanpa kehilangan yang membedakannya. */
function format(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toFixed(0);
  if (abs >= 10) return value.toFixed(2);
  if (abs >= 1) return value.toFixed(3);
  return value.toFixed(4);
}

/**
 * Ukuran jendela ini, dan batasnya.
 *
 * Kotaknya dulu tetap: 640 piksel lebar, 340 tinggi. Itu cukup untuk
 * memastikan sebuah kurva memang berisi, dan tidak cukup untuk pertanyaan
 * berikutnya — membandingkan dua versi kurva yang sama pada selang yang
 * sempit, atau membaca tabelnya lebih dari selusin baris sekaligus. Yang
 * dipilih karena itu bukan angka lain melainkan angka yang bisa diubah, dan
 * disimpan: orang yang melebarkannya sekali biasanya melebarkannya untuk
 * pekerjaan itu, bukan untuk satu kurva.
 *
 * Batas bawahnya adalah ukuran ketika jendela ini masih menjawab sesuatu;
 * batas atasnya adalah layarnya sendiri, dikurangi tempat bagi kepala jendela,
 * pemilih tampilan, dan baris statistik di kakinya.
 */
const MIN_W = 460;
const MIN_H = 200;
/** Baris angka sumbu di bawah kotak gambar; tabelnya ikut memakainya. */
const AXIS_ROW_H = 13;

const clamp = (value: number, lo: number, hi: number) =>
  Math.round(Math.min(hi, Math.max(lo, value)));

export default function CurveInspector({
  curveId,
  mnemonic,
  color,
  versions,
  onClose,
  mayEdit,
  focusMd,
  onSaved,
}: {
  curveId: number;
  mnemonic: string;
  color: string;
  versions: { id: number; label: string }[];
  onClose: () => void;
  /** Peran yang boleh menulis. Tanpa ini jendela ini hanya dibaca. */
  mayEdit?: boolean;
  /**
   * Kedalaman yang dituju, bila jendela ini dibuka dari lembar log: tabelnya
   * membuka diri pada baris itu dan menyalakan penyuntingnya. Sebuah menu
   * berbunyi "sunting nilai di 2072,5 m" yang memulangkan tabel di baris
   * pertama menyuruh penggunanya mencari sendiri kedalaman yang baru saja ia
   * tunjuk.
   */
  focusMd?: number;
  /** Dipanggil sesudah versi baru ditulis: pohon dan lembarnya dimuat ulang. */
  onSaved?: (result: { version: number; points: number; setName: string }) => void;
}) {
  const [view, setView] = useState<"grafik" | "tabel">(focusMd === undefined ? "grafik" : "tabel");
  const [size, setSize] = useStored("evolog.inspect.size", { w: 640, h: 340 });
  /**
   * Penyuntingan angka.
   *
   * `editing` menyalakannya, dan sekaligus meminta kurvanya utuh: tabel yang
   * dijarangkan tidak memuat setiap cuplikan, dan sebuah spike yang hendak
   * dibuang justru bisa jatuh di antara dua baris yang tercetak.
   *
   * `draft` menahan suntingan sampai disimpan, dikunci pada kedalamannya.
   * Menyimpan tiap ketikan berarti satu versi set kurva per angka, dan riwayat
   * yang berisi dua puluh versi untuk satu spike bukan riwayat.
   */
  const [editing, setEditing] = useState(focusMd !== undefined);
  const [draft, setDraft] = useState<Map<number, number | null>>(new Map());
  const [cell, setCell] = useState<number | null>(null);
  /**
   * Setelan tampilan kurva ini — warna, bentuk garis, ketebalan, kedua ujung
   * skala, sumbu, arsiran.
   *
   * Disimpan per mnemonic dan bukan per kurva: yang membuka GR versi lama
   * sesudah menyetel GR versi baru sedang membandingkan dua versi kurva yang
   * sama, dan dua setelan berbeda membuat perbandingan itu mustahil justru
   * pada saat ia paling diperlukan.
   */
  const [cfg, setCfg] = useStored<InspectConfig>(`evolog.inspect.track.${mnemonic}`, {});
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  /** Kedalaman yang sedang dibaca kursor di atas track. */
  const [hover, setHover] = useState<number | null>(null);
  const trackBox = useRef<HTMLDivElement | null>(null);
  /** Tombol sedang ditekan — seretan, bukan satu ketukan. */
  const seret = useRef(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [compareId, setCompareId] = useState<number | null>(null);
  // Dijarangkan di server. Sebuah kurva sepuluh ribu cuplikan digambar pada
  // kotak setinggi empat ratus piksel, dan seribu titik sudah lebih rapat
  // daripada yang bisa dibedakan mata; tabelnya pun tidak dibaca baris demi
  // baris sampai sepuluh ribu.
  const curve = useAsync(
    (signal) =>
      api.get<CurveData>(
        `/api/curves/${curveId}/data/`,
        // Utuh ketika sedang disunting, dijarangkan ketika hanya dibaca.
        { decimate: editing ? 200000 : 1200 },
        signal,
      ),
    [curveId, editing],
  );

  const other = useAsync(
    (signal) =>
      compareId === null
        ? Promise.resolve(null)
        : api.get<CurveData>(`/api/curves/${compareId}/data/`, { decimate: 1200 }, signal),
    [compareId],
  );
  const compareLabel = versions.find((v) => v.id === compareId)?.label ?? null;

  const stats = useMemo(() => {
    const values = (curve.data?.values ?? []).filter((v): v is number => v !== null && Number.isFinite(v));
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const sum = values.reduce((a, b) => a + b, 0);
    return {
      n: values.length,
      // Rumpang dihitung dan disebutkan: kurva yang separuh isinya kosong
      // terlihat sama saja dengan kurva utuh pada grafik sekecil ini.
      blank: (curve.data?.values.length ?? 0) - values.length,
      min: sorted[0],
      max: sorted[sorted.length - 1],
      mean: sum / values.length,
      p50: sorted[Math.floor(sorted.length / 2)],
    };
  }, [curve.data]);

  /**
   * Nilai kurva dengan suntingan yang belum disimpan sudah diterapkan.
   *
   * Satu draft, dua tampilan. Sebelumnya grafik membaca `data.values` mentah
   * sementara tabel membaca draftnya, jadi sebuah angka yang baru disunting
   * tampak berubah di tabel dan tidak berubah sedikit pun di grafik — dua
   * jendela yang mengaku menampilkan kurva yang sama sambil menampilkan dua
   * kurva yang berbeda, dan yang mempercayai grafiknya akan menyimpan
   * suntingan yang ia kira tidak jadi.
   */
  /** Nilai kurva dengan suntingan yang belum disimpan sudah diterapkan. */
  const applied = useMemo(
    () => (curve.data ? applyDraft(curve.data.depths, curve.data.values, draft) : []),
    [curve.data, draft],
  );

  const plot = useMemo(() => {
    const data = curve.data;
    if (!data || !stats) return null;
    const rival = other.data;
    // Kedua versi berbagi satu sumbu nilai dan satu sumbu kedalaman. Dua sumbu
    // yang masing-masing pas pada datanya sendiri akan menumpuk dua kurva yang
    // berbeda menjadi satu bentuk yang sama, dan perbandingannya lenyap justru
    // karena keduanya digambar sebaik-baiknya.
    const rivalValues = (rival?.values ?? []).filter(
      (v): v is number => v !== null && Number.isFinite(v));
    const lo = Math.min(stats.min, ...(rivalValues.length ? [Math.min(...rivalValues)] : []));
    const hi = Math.max(stats.max, ...(rivalValues.length ? [Math.max(...rivalValues)] : []));
    const top = Math.min(data.depths[0], ...(rival ? [rival.depths[0]] : []));
    const base = Math.max(
      data.depths[data.depths.length - 1],
      ...(rival ? [rival.depths[rival.depths.length - 1]] : []),
    );
    return { lo, hi, top, base };
  }, [curve.data, other.data, stats]);

  /**
   * Skala kedalaman jendela ini — mesin yang sama dengan lembar log.
   *
   * Roda memperbesar di sekitar kursor, seret pada mistar meregang, sapuan
   * tegak di atas kurva memilih satu selang. Bukan tiga gerakan baru melainkan
   * gerakan yang sudah dipakai di layar Log dan layar korelasi: sebuah kurva
   * yang diperbesar dengan cara lain di jendela yang lain adalah dua perilaku
   * yang salah satunya akan dipelajari orang dua kali.
   *
   * `gridPx` membulatkan tinggi lembar ke kelipatan kisi mendatarnya, sehingga
   * garis kisi tidak pernah jatuh di antara dua kedudukan dan petak terbawah
   * tidak pernah buntung. `skipBoxOn` menutup lapisan penyuntingan: seret di
   * atasnya menulis nilai, dan yang menulis nilai tidak sedang memilih selang.
   */
  const {
    attachPane,
    sheet,
    scaleMode,
    setScaleMode,
    stretch,
    boxHandlers,
    box,
    stepGrid,
  } = useDepthSheet({
    storageKey: "evolog.inspect.scale",
    metres: plot ? Math.abs(plot.base - plot.top) : 0,
    range: plot ? [plot.top, plot.base] : null,
    gridPx: DEPTH_GRID_PX,
    skipBoxOn: "[data-edit-layer]",
  });

  /**
   * Track lembar log untuk kurva ini — bentuk yang sama dengan yang digambar
   * layar Log dan lembar korelasi.
   *
   * Sebelumnya jendela ini menggambar SVG-nya sendiri: sebuah jalur, beberapa
   * garis kisi, dan tidak ada yang lain. Yang hilang bukan hiasan melainkan
   * cara membaca — arsiran yang memperlihatkan sisi mana dari cutoff sebuah
   * selang berada, sumbu logaritmik yang membuat resistivitas berhenti
   * menempel di tepi kiri, dan kepala yang menyebutkan kedua ujung skalanya.
   * Menyalinnya kembali ke sini berarti dua penggambar kurva yang cepat atau
   * lambat berselisih; memakai yang sudah ada berarti setiap kurva terbaca
   * sama di mana pun ia dibuka.
   */
  const spec = useMemo<TrackSpec | null>(() => {
    const data = curve.data;
    if (!data || !plot) return null;
    const log = cfg.logarithmic ?? suggestedScale(mnemonic) === "log";
    const [min, max] = resolveAxisRange(
      cfg.min ?? data.display_min ?? plot.lo,
      cfg.max ?? data.display_max ?? plot.hi,
      log ? "log" : "linear",
    );
    const curves: TrackCurve[] = [
      {
        name: mnemonic,
        key: "utama",
        color: cfg.color ?? color,
        min,
        max,
        unit: data.unit,
        style: cfg.style ?? "solid",
        width: cfg.width,
        logarithmic: log,
        fill: cfg.fill ?? undefined,
        customScale: cfg.min !== undefined || cfg.max !== undefined,
        depths: data.depths,
        values: applied,
      },
    ];
    // Pembanding digambar putus-putus dan tanpa arsiran: yang sedang diperiksa
    // adalah kurva utamanya, dan arsiran kedua akan menutupinya.
    if (other.data) {
      curves.push({
        name: compareLabel ?? "comparison",
        key: "pembanding",
        color: "var(--ink-5)",
        min,
        max,
        unit: other.data.unit,
        style: "dashed",
        logarithmic: log,
        depths: other.data.depths,
        values: other.data.values,
      });
    }
    return {
      id: mnemonic,
      title: mnemonic,
      units: data.unit ?? "",
      flex: 1,
      logarithmic: log,
      curves,
    };
  }, [curve.data, other.data, applied, cfg, plot, mnemonic, color, compareLabel]);

  const rows = useMemo(() => {
    const data = curve.data;
    if (!data) return [];
    const rival = other.data;
    return data.depths.map((md, index) => {
      const value = data.values[index] ?? null;
      const against = rival ? sampleAt(rival.depths, rival.values, md) : null;
      return {
        md,
        value,
        against,
        delta:
          value !== null && against !== null && Number.isFinite(value) && Number.isFinite(against)
            ? value - against
            : null,
      };
    });
  }, [curve.data, other.data]);

  /** Ringkasan selisih, dihitung hanya pada kedalaman yang keduanya berangka. */
  const diff = useMemo(() => {
    const deltas = rows.map((r) => r.delta).filter((d): d is number => d !== null);
    if (!deltas.length) return null;
    const sorted = [...deltas].sort((a, b) => a - b);
    return {
      n: deltas.length,
      median: sorted[Math.floor(sorted.length / 2)],
      mean: deltas.reduce((a, b) => a + b, 0) / deltas.length,
      max: sorted[sorted.length - 1],
      min: sorted[0],
    };
  }, [rows]);

  /**
   * Baris yang dituju ketika jendela ini dibuka dari lembar log.
   *
   * Kedalaman yang datang dari lembar adalah tempat jari menekan, bukan
   * kedalaman sebuah cuplikan; yang dicari di sini adalah cuplikan terdekat —
   * cuplikan yang sama dengan yang akan disunting server nanti.
   */
  const focusRow = useMemo(() => {
    if (focusMd === undefined || rows.length === 0) return null;
    let best = rows[0].md;
    for (const row of rows) {
      if (Math.abs(row.md - focusMd) < Math.abs(best - focusMd)) best = row.md;
    }
    return best;
  }, [rows, focusMd]);

  // Sekali saja, ketika barisnya sudah ada: membukanya lagi pada tiap render
  // akan merebut kembali kursor dari sel lain yang sedang diketik.
  const focused = useRef(false);
  const scrolled = useRef(false);
  useEffect(() => {
    if (focusRow === null || focused.current) return;
    focused.current = true;
    setCell(focusRow);
    const value = rows.find((row) => row.md === focusRow)?.value ?? null;
    setTyped(value === null ? "" : String(value));
  }, [focusRow, rows]);

  /**
   * Menuliskan satu nilai dari kedudukan kursor di atas track.
   *
   * Kedalamannya dibulatkan ke cuplikan terdekat, bukan dipakai apa adanya:
   * yang disimpan server adalah nilai pada cuplikan, dan sebuah suntingan yang
   * dialamatkan ke kedalaman di antara dua cuplikan akan menambah cuplikan
   * baru yang tidak pernah diukur siapa pun.
   *
   * Nilainya dihitung lewat `unproject`, pembalik pemetaan yang dipakai track
   * itu sendiri menggambar. Menghitungnya sebagai pecahan lebar dikali rentang
   * benar hanya pada sumbu linear; pada resistivitas yang logaritmik, seretan
   * ke tengah track akan menuliskan angka yang meleset satu dekade — dan tidak
   * ada yang kelihatan salah sampai seseorang membaca angkanya.
   */
  const suntingDi = (event: { clientX: number; clientY: number }) => {
    const box = trackBox.current?.getBoundingClientRect();
    const data = curve.data;
    if (!box || !box.width || !box.height || !data || !plot || !spec) return;
    const c = spec.curves[0];
    const hasil = editAt({
      depths: data.depths,
      fx: (event.clientX - box.left) / box.width,
      fy: (event.clientY - box.top) / box.height,
      top: plot.top,
      base: plot.base,
      min: c.min,
      max: c.max,
      logarithmic: c.logarithmic,
    });
    if (!hasil) return;
    setDraft((sebelumnya) => new Map(sebelumnya).set(hasil.md, hasil.value));
  };

  const simpan = async () => {
    if (draft.size === 0) return;
    setSaving(true);
    setProblem(null);
    try {
      const result = await api.post<{ version: number; points: number }>(
        `/api/curves/${curveId}/edits/`,
        { edits: [...draft].map(([md, value]) => ({ md, value })) },
      );
      setDraft(new Map());
      onSaved?.({ version: result.version, points: result.points, setName: mnemonic });
      onClose();
    } catch (cause) {
      // Di dalam jendelanya, bukan sebagai toast yang lewat: yang gagal adalah
      // pekerjaan yang masih ada di layar ini, dan suntingannya masih utuh.
      setProblem(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  /** Ketikan yang tidak bisa dibaca sebagai angka, dan bukan pula kosong. */
  const badEntry =
    typed.trim() !== "" && !Number.isFinite(Number(typed.trim().replace(",", ".")));

  /**
   * Menutup penyunting satu sel, dan menyimpan ketikannya ke draft.
   *
   * Ketikan yang tidak bisa dibaca tidak menutup selnya dan tidak menulis apa
   * pun: sel yang menutup diri sambil memulangkan angka lamanya terbaca
   * sebagai suntingan yang tersimpan, dan angka itulah yang kemudian dipakai
   * orang menghitung.
   */
  const commit = (md: number) => {
    if (badEntry) return;
    const text = typed.trim();
    setDraft((current) => {
      const next = new Map(current);
      if (text === "") {
        // Kosong berarti hilang, bukan nol. Sebuah cuplikan bernilai nol dan
        // sebuah cuplikan yang tidak terukur adalah dua hal yang berbeda, dan
        // hanya yang kedua boleh dilewati perhitungan.
        next.set(md, null);
      } else {
        next.set(md, Number(text.replace(",", ".")));
      }
      return next;
    });
    setCell(null);
    setTyped("");
  };

  return (
    <Modal
      title={`${mnemonic} — curve contents`}
      onClose={onClose}
      width={size.w}
      // Jendela ini duduk di tengah layar: tepi kanannya bergerak setengah
      // dari pertambahan lebarnya, jadi simpangan tapak digandakan supaya
      // sudutnya tetap menempel pada jari yang menyeretnya. Tingginya tidak —
      // yang tumbuh ke bawah adalah kotak gambar di dalamnya, bukan jendela
      // yang tumbuh ke dua arah.
      onResize={(dx, dy) =>
        setSize({
          w: clamp(size.w + dx * 2, MIN_W, Math.max(MIN_W, window.innerWidth - 48)),
          h: clamp(size.h + dy, MIN_H, Math.max(MIN_H, window.innerHeight - 260)),
        })
      }
      footer={
        draft.size > 0 ? (
          <>
            <span style={{ flex: 1, font: "400 10.5px var(--font-sans)", color: "var(--ink-5)" }}>
              {draft.size} points edited, not saved yet.
            </span>
            <Button onClick={() => setDraft(new Map())}>Discard edits</Button>
            {/* Namanya menyebutkan apa yang akan terjadi pada datanya, bukan
                "Simpan": hasilnya adalah versi baru set kurva ini, dan yang
                lama tetap ada — itu janji yang pantas dibaca sebelum ditekan. */}
            <Button variant="primary" busy={saving} onClick={simpan}>
              Save as a new version
            </Button>
          </>
        ) : undefined
      }
    >
      {curve.loading && <Loading label="Reading samples…" />}
      {curve.error && <Notice tone="error">{curve.error}</Notice>}
      {problem && <Notice tone="error">{problem}</Notice>}
      {curve.data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Segmented
              value={view}
              options={[
                { value: "grafik" as const, label: "Graph" },
                { value: "tabel" as const, label: "Table" },
              ]}
              onChange={setView}
            />
            {/* Berlaku pada kedua tampilan. Penyuntingan menulis ke satu draft
                yang sama, jadi membatasinya pada tabel berarti angka yang sama
                bisa disunting di satu tempat dan tidak di tempat lain — sebuah
                pembatasan yang tidak punya alasan selain urutan pengerjaannya
                dulu. Di grafik yang disunting adalah bentuknya, di tabel
                angkanya; keduanya menyunting kurva yang sama. */}
            {mayEdit && (
              <Button
                variant={editing ? "primary" : "ghost"}
                onClick={() => {
                  setEditing(!editing);
                  setCell(null);
                  if (editing) setDraft(new Map());
                }}
                title={
                  editing
                    ? "Stop editing; edits that were not saved are discarded"
                    : view === "grafik"
                      ? "Edit this curve by dragging across it"
                      : "Edit this curve's numbers"
                }
              >
                {editing ? "Done editing" : "Edit values"}
              </Button>
            )}
            <span style={{ flex: 1 }} />
            {versions.length > 0 ? (
              <div style={{ width: 208 }}>
                <Dropdown
                  value={compareId === null ? "" : String(compareId)}
                  options={[
                    { value: "", label: "No comparison" },
                    ...versions.map((v) => ({ value: String(v.id), label: `Against: ${v.label}` })),
                  ]}
                  onChange={(value) => setCompareId(value ? Number(value) : null)}
                  width="100%"
                  compact
                />
              </div>
            ) : (
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {curve.data.description || curve.data.unit || "—"}
              </span>
            )}
          </div>

          {!stats && <Empty>This curve has no numeric sample at all.</Empty>}

          {stats && view === "grafik" && plot && spec && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {/* Kepala track lembar log, lengkap dengan kedua ujung skala,
                  satuan, dan lencana LIN/LOG yang bisa ditekan. Baris legenda
                  di dalamnya membuka penyetel warna, bentuk garis, ketebalan
                  dan arsiran — persis kotak yang sama dengan di layar Log. */}
              <div style={{ display: "flex" }}>
                <div style={{ width: DEPTH_GUTTER_W, flex: "none" }} />
                <TrackHeader
                  track={spec}
                  minWidth={0}
                  onCurveEdit={mayEdit ? (_id, key, patch) => {
                    // Hanya kurva utama yang setelannya milik jendela ini;
                    // pembanding adalah versi lain yang sedang dipinjam, dan
                    // menyetelnya di sini akan menuliskan setelan kurva itu ke
                    // kunci mnemonic yang sedang dibuka.
                    if (key !== "utama") return;
                    setCfg({ ...cfg, ...(patch as InspectConfig),
                             fill: "fill" in patch ? (patch.fill ?? null) : cfg.fill });
                  } : undefined}
                  onCurveAuto={(_id, key) => {
                    if (key !== "utama") return;
                    setCfg({ ...cfg, min: undefined, max: undefined });
                  }}
                  onScaleToggle={() => setCfg({ ...cfg, logarithmic: !spec.logarithmic })}
                />
              </div>

              {/* Panel yang bergulir, dan di dalamnya lembar setinggi skalanya.
                  Dalam mode pas keduanya setinggi sama dan tidak ada yang
                  bergulir — persis jendela ini sebelum ia bisa diperbesar. */}
              <div
                ref={attachPane}
                className="scroll-y"
                style={{ height: size.h, overflowY: "auto", overflowX: "hidden", display: "flex" }}
              >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "stretch",
                  height: sheet.fitted ? "100%" : sheet.heightPx,
                }}
              >
                <DepthGutter
                  top={plot.top}
                  base={plot.base}
                  ticks={depthTickCount(sheet.heightPx)}
                  cursor={hover}
                  onStretch={stretch}
                />
                <div
                  ref={trackBox}
                  {...boxHandlers}
                  // Wadah flex, bukan sekadar kotak berposisi. Akar `LogTrack`
                  // bergaya `flex: track.flex` tanpa tinggi sendiri, dan
                  // seluruh isinya digambar `position: absolute; inset: 0` —
                  // jadi di dalam induk yang bukan flex ia setinggi nol dan
                  // tidak menggambar apa pun: kepala track dan mistar
                  // kedalaman tetap muncul, dan yang hilang justru kurvanya.
                  style={{ flex: 1, minWidth: 0, position: "relative", display: "flex" }}
                >
                  <LogTrack
                    track={spec}
                    top={plot.top}
                    base={plot.base}
                    minWidth={0}
                    cursorDepth={hover}
                    onHoverDepth={setHover}
                  />
                  {/* Lapisan penyuntingan. Dipasang hanya ketika penyuntingan
                      menyala: sebuah lapisan yang selalu ada akan menelan
                      pembacaan kursor track di bawahnya, dan yang membaca
                      kurvanya tidak sedang meminta apa pun untuk berubah. */}
                  {editing && (
                    <div
                      role="application"
                      data-edit-layer
                      aria-label={`Edit ${mnemonic} by dragging`}
                      onPointerDown={(event) => {
                        event.currentTarget.setPointerCapture(event.pointerId);
                        seret.current = true;
                        suntingDi(event);
                      }}
                      onPointerMove={(event) => {
                        if (seret.current) suntingDi(event);
                      }}
                      onPointerUp={(event) => {
                        seret.current = false;
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      }}
                      onPointerCancel={() => {
                        seret.current = false;
                      }}
                      style={{ position: "absolute", inset: 0, cursor: "crosshair",
                               touchAction: "none" }}
                    />
                  )}
                  {/* Titik yang sudah disunting, supaya yang menyeret melihat
                      apa yang sudah ia ubah tanpa berpindah ke tabel. */}
                  {draft.size > 0 && (
                    // Titik digambar sebagai elemen berposisi persen, bukan
                    // `<circle>` di dalam SVG. Kotak track direntangkan dengan
                    // `preserveAspectRatio="none"` supaya kurvanya mengisi
                    // lebar berapa pun, dan sebuah lingkaran di dalamnya ikut
                    // teregang menjadi lonjong — makin sempit jendelanya makin
                    // pipih, sampai penandanya terbaca sebagai coretan alih-alih
                    // sebagai titik.
                    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden>
                      {[...draft].map(([md, value]) => {
                        if (value === null || !Number.isFinite(value)) return null;
                        const c = spec.curves[0];
                        const fx = valueFraction(value, c.min, c.max, c.logarithmic);
                        if (fx === null) return null;
                        const fy = (md - plot.top) / ((plot.base - plot.top) || 1);
                        if (fy < 0 || fy > 1) return null;
                        return (
                          <span
                            key={md}
                            style={{
                              position: "absolute",
                              left: `${fx * 100}%`,
                              top: `${fy * 100}%`,
                              width: 7,
                              height: 7,
                              marginLeft: -3.5,
                              marginTop: -3.5,
                              borderRadius: "50%",
                              background: "var(--accent)",
                              border: "1px solid var(--canvas)",
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                  {/* Kotak yang sedang disapu, berikut kedua kedalaman yang
                      dijepitnya: sapuan yang tidak menyebutkan apa yang akan
                      dipilihnya menuntut penggunanya menebak. */}
                  {box && (
                    <BoxZoomOverlay
                      from={box.from}
                      to={box.to}
                      height={sheet.heightPx}
                      top={plot.top}
                      base={plot.base}
                      format={(depth) => `${depth.toFixed(1)} m`}
                    />
                  )}
                </div>
              </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "5px 0 0",
                            font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
                {compareLabel && (
                  <>
                    <span style={{ color: cfg.color ?? color }}>— {mnemonic} as opened</span>
                    <span>-- {compareLabel}</span>
                  </>
                )}
                {editing && (
                  <span style={{ color: "var(--ink-6)" }}>
                    Drag sideways across the curve to change its value at that
                    depth; the result joins the same draft as the table.
                  </span>
                )}
                <span style={{ flex: 1 }} />
                {/* Skala yang benar-benar berlaku, dan kedua tombol yang
                    mengubahnya satu petak kisi sekali tekan. Angkanya dibaca
                    dari lembar dan bukan dari mode: sesudah tingginya
                    dibulatkan ke kisi, yang berlaku bukan lagi angka yang
                    diminta. */}
                <span style={{ color: "var(--ink-6)" }}>
                  1:{sheet.scale}
                  {sheet.fitted ? " · fit" : ""}
                </span>
                <Button
                  variant="ghost"
                  style={{ height: 20 }}
                  title="Stretch by one grid step"
                  onClick={() => stepGrid(1)}
                >
                  +
                </Button>
                <Button
                  variant="ghost"
                  style={{ height: 20 }}
                  title="Compress by one grid step"
                  onClick={() => stepGrid(-1)}
                >
                  −
                </Button>
                {scaleMode !== "fit" && (
                  <Button
                    variant="ghost"
                    style={{ height: 20 }}
                    title="Back to one screen"
                    onClick={() => setScaleMode("fit")}
                  >
                    Fit
                  </Button>
                )}
              </div>
            </div>
          )}

          {stats && view === "tabel" && (
            <div className="scroll-y" style={{ height: size.h + AXIS_ROW_H, border: "1px solid var(--line-strong)",
                                               borderRadius: 5 }}>
              <table style={{ width: "100%", borderCollapse: "collapse",
                              font: "400 10.5px var(--font-mono)" }}>
                <thead>
                  <tr>
                    {[
                      "MD (m)",
                      `${mnemonic}${curve.data.unit ? ` (${curve.data.unit})` : ""}`,
                      ...(compareLabel ? [compareLabel, "difference"] : []),
                    ].map((h) => (
                      <th key={h} style={{ position: "sticky", top: 0, textAlign: "right",
                                           padding: "5px 11px", background: "var(--panel)",
                                           borderBottom: "1px solid var(--line-strong)",
                                           font: "600 9.5px var(--font-sans)", color: "var(--ink-5)" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.md}
                      ref={
                        row.md === focusRow
                          ? (element) => {
                              if (!element || scrolled.current) return;
                              scrolled.current = true;
                              element.scrollIntoView({ block: "center" });
                            }
                          : undefined
                      }
                    >
                      <td style={{ textAlign: "right", padding: "2px 11px", color: "var(--ink-5)" }}>
                        {row.md.toFixed(2)}
                      </td>
                      <td
                        onClick={editing ? () => {
                          setCell(row.md);
                          const staged = draft.get(row.md);
                          const shown = staged !== undefined ? staged : row.value;
                          setTyped(shown === null ? "" : String(shown));
                        } : undefined}
                        style={{
                          textAlign: "right",
                          padding: "2px 11px",
                          color: draft.has(row.md)
                            ? "var(--accent)"
                            : row.value === null ? "var(--ink-7)" : "var(--ink-2)",
                          // Yang sudah disunting ditandai sampai disimpan:
                          // sebuah angka yang berubah tanpa jejak adalah angka
                          // yang tidak bisa diperiksa kembali sebelum ditulis.
                          background: draft.has(row.md) ? "var(--accent-wash)" : undefined,
                          cursor: editing ? "text" : undefined,
                        }}
                      >
                        {cell === row.md ? (
                          <input
                            autoFocus
                            value={typed}
                            aria-label={`Value at ${row.md.toFixed(2)} m`}
                            // Terpilih seluruhnya begitu terbuka: sel ini
                            // dibuka untuk mengganti angkanya, dan ketikan yang
                            // menempel di belakang angka lama menghasilkan
                            // "0,06740,123" — bukan angka, dan bukan pula yang
                            // dimaksudkan siapa pun.
                            onFocus={(event) => event.currentTarget.select()}
                            onChange={(event) => setTyped(event.target.value)}
                            onBlur={() => commit(row.md)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") commit(row.md);
                              if (event.key === "Escape") {
                                setCell(null);
                                setTyped("");
                              }
                            }}
                            style={{
                              width: "100%",
                              textAlign: "right",
                              background: "var(--control)",
                              border: `1px solid ${badEntry ? "var(--rose)" : "var(--accent)"}`,
                              borderRadius: 3,
                              font: "400 10.5px var(--font-mono)",
                              color: "var(--ink)",
                              outline: "none",
                            }}
                          />
                        ) : draft.has(row.md) ? (
                          format(draft.get(row.md) ?? null)
                        ) : (
                          format(row.value)
                        )}
                      </td>
                      {compareLabel && (
                        <>
                          <td style={{ textAlign: "right", padding: "2px 11px",
                                       color: row.against === null ? "var(--ink-7)" : "var(--ink-4)" }}>
                            {format(row.against)}
                          </td>
                          <td style={{ textAlign: "right", padding: "2px 11px",
                                       color: row.delta === null ? "var(--ink-7)" : "var(--accent)" }}>
                            {row.delta === null ? "—" : `${row.delta >= 0 ? "+" : ""}${format(row.delta)}`}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {stats && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
              <KeyValue k="Samples" v={`${stats.n.toLocaleString("en-US")} numeric`} />
              <KeyValue k="Gaps" v={stats.blank ? stats.blank.toLocaleString("en-US") : "none"} />
              <KeyValue k="Smallest" v={format(stats.min)} />
              <KeyValue k="Largest" v={format(stats.max)} />
              <KeyValue k="Mean" v={format(stats.mean)} />
              <KeyValue k="Median" v={format(stats.p50)} />
            </div>
          )}

          {compareLabel && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
              {diff ? (
                <>
                  <KeyValue k="Median difference" v={`${diff.median >= 0 ? "+" : ""}${format(diff.median)}`} />
                  <KeyValue k="Mean difference" v={`${diff.mean >= 0 ? "+" : ""}${format(diff.mean)}`} />
                  <KeyValue k="Smallest difference" v={format(diff.min)} />
                  <KeyValue k="Largest difference" v={format(diff.max)} />
                  {/* Cacahnya disebut: selisih yang dihitung dari seperlima
                      kedalaman bukan selisih kurvanya. */}
                  <KeyValue k="Compared" v={`${diff.n.toLocaleString("en-US")} depths`} />
                </>
              ) : (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)" }}>
                  No depth carries a number on both curves at once.
                </span>
              )}
            </div>
          )}

          {/* Yang ditampilkan sudah dijarangkan, dan cacah aslinya disebut
              supaya tabel seribu baris tidak terbaca sebagai seluruh isinya. */}
          <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
            {curve.data.count.toLocaleString("en-US")} samples at a step of{" "}
            {curve.data.depth_step.toFixed(4)} m, {curve.data.depth_start.toFixed(1)}–
            {curve.data.depth_stop.toFixed(1)} m MD
            {curve.data.depths.length < curve.data.count
              ? ` · showing ${curve.data.depths.length.toLocaleString("en-US")} evenly decimated`
              : ""}
          </span>
        </div>
      )}
    </Modal>
  );
}
